import React from 'react';
import styled from 'styled-components';
import SmallDoughnut from '@/common/components/SmallDoughnut';
import { PILLAR_QUESTION_MAP } from '@/common/constants/pillars';
import { PillarData } from '@/areas/culture-hub/types/api';
import { H3, PageSection } from '@/common/styles/page';
import { scoreToColor, getSentimentSymbol } from '@/areas/dashboard/components/DashboardTeam.utils';

const PillarCardContainer = styled.div<{ $selected?: boolean }>`
  background: #ffffff;
  border: 1px solid ${props => (props.$selected ? '#3b82f6' : '#e5e7eb')};
  border-radius: 1rem;
  box-shadow: ${props => (props.$selected ? '0 8px 20px rgba(59, 130, 246, 0.18)' : '0 1px 2px rgba(15, 23, 42, 0.08)')};
  transition: transform 0.18s cubic-bezier(.2, .9, .2, 1), box-shadow 0.2s ease, border-color 0.2s ease;
  width: 100%;
  min-height: 10rem;
`;

const PillarCardTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;
  min-width: 0;
`;

const PillarCardLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1 1 auto;
  min-width: 0;
`;

const PillarCardTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 auto;
  min-width: 0;
`;

const QuestionText = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
  min-width: 0;
`;

const ThemePreviewRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.45rem;
`;

const ThemePreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

const ThemeStatusIcon = styled.span<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.$color};
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
  font-weight: 700;
  flex-shrink: 0;
`;

const ThemeLabel = styled.span`
  color: #475569;
  font-size: 0.79rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


export const DashboardPillarCard: React.FC<{
  item: PillarData;
  onOpen?: (k: string) => void;
  showQuestion?: boolean;
  selected?: boolean;
}> = ({ item, onOpen, showQuestion = false, selected = false }) => {
  const interactive = typeof onOpen === 'function';
  const handleClick = () => {
    if (interactive) onOpen(item.key);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(item.key);
    }
  };

  const themeItems = (() => {
    const themes = Array.isArray(item.themes) ? item.themes : [];

    const scoredThemes = themes
      .map((theme) => ({
        label: (theme?.label || '').trim(),
        score: typeof theme?.sentiment === 'number' ? theme.sentiment : undefined,
      }))
      .filter((theme): theme is { label: string; score: number } => Boolean(theme.label) && typeof theme.score === 'number');

    if (scoredThemes.length > 0) {
      const sortedByScore = scoredThemes.slice().sort((a, b) => b.score - a.score);
      if (sortedByScore.length >= 3) {
        const middleIndex = Math.floor((sortedByScore.length - 1) / 2);
        return [sortedByScore[0], sortedByScore[middleIndex], sortedByScore[sortedByScore.length - 1]];
      }
      return sortedByScore;
    }

    return themes
      .map((theme) => ({
        label: (theme?.label || '').trim(),
        score: undefined,
      }))
      .filter((theme) => Boolean(theme.label))
      .slice(0, 3);
  })();


  return (
    <PillarCardContainer
      {...(interactive ? { role: 'button', tabIndex: 0, onClick: handleClick, onKeyDown: handleKeyDown } : {})}
      aria-label={interactive ? `Open pillar ${item.name}` : undefined}
      $selected={selected}
    >
      <PageSection style={{ padding: '0.75rem 1rem', minWidth: 0 }}>
        <PillarCardTitleRow>
          <PillarCardLeft>
            <SmallDoughnut value={item.score} colorized size={44} />
            <PillarCardTitleBlock>
              <H3>{item.name}</H3>
              {showQuestion && PILLAR_QUESTION_MAP[item.name] ? (
                <QuestionText>
                  {PILLAR_QUESTION_MAP[item.name]}
                </QuestionText>
              ) : null}
              {themeItems.length > 0 ? (
                <ThemePreviewRow>
                  {themeItems.map((theme) => (
                    <ThemePreviewItem key={`${item.key}-${theme.label}`} title={theme.label}>
                      <ThemeStatusIcon $color={scoreToColor(typeof theme.score === 'number' ? theme.score * 10 : 0)}>
                        {getSentimentSymbol(typeof theme.score === 'number' ? theme.score * 10 : 0)}
                      </ThemeStatusIcon>
                      <ThemeLabel>{theme.label}</ThemeLabel>
                    </ThemePreviewItem>
                  ))}
                </ThemePreviewRow>
              ) : null}
            </PillarCardTitleBlock>
          </PillarCardLeft>
        </PillarCardTitleRow>
      </PageSection>
    </PillarCardContainer>
  );
};
